import { Alert } from "@mui/material";
import { Plus } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import { TriggerNameLink } from "@/shared/components/links";
import {
  qualifyPrincipal,
  renderPrincipal,
  ResourceView,
  useResourceView,
  type Filter,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { useAgentDirectory } from "@/shared/domain/agents/hooks";
import {
  eventLabel,
  hasActiveFilters,
  isSchedule,
  readableCron,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type FiresOnFilter,
  type PaginationState,
  type TriggerFilterParams,
  type TriggerTargetSpec,
  type TriggerV2Record,
} from "@/shared/domain/triggers";
import { useTriggers } from "@/shared/domain/triggers/hooks";
import { Toggle, ToggleGroup } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import {
  cellStyles,
  createColumnHelper,
  DataTable,
  FLEX_FILL_COL_DEF,
  renderDashOrValue,
  type ColDef,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";

import { TriggerEnabledSwitch } from "./trigger/TriggerEnabledSwitch";
import { TRIGGER_FILTER_DEFINITIONS } from "./trigger/triggerFilterDefinitions";
import styles from "./Triggers.module.css";

/** URL search param carrying the quick "fires on" filter, so the view is linkable. */
const FIRES_ON_PARAM = "fires";

const NO_FILTERS: readonly Filter[] = [];
const NO_TRIGGERS: TriggerV2Record[] = [];

function parseFiresOn(raw: string | null): FiresOnFilter {
  return raw === "events" || raw === "schedule" ? raw : "all";
}

function targetSummary(
  target: TriggerTargetSpec,
  agentNames: ReadonlyMap<string, string>,
): string {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return `${TARGET_TYPE_LABELS[target.type]}: ${target.action.name}`;
    case TriggerTargetType.StartAgent:
      return `${TARGET_TYPE_LABELS[target.type]}: ${
        agentNames.get(target.agent_id) ?? target.agent_id
      }`;
    case TriggerTargetType.SendSlackMessage:
      return TARGET_TYPE_LABELS[target.type];
  }
}

function firesOnSummary(trigger: TriggerV2Record): string {
  return isSchedule(trigger.fires_on)
    ? readableCron(trigger.fires_on.cron)
    : trigger.fires_on.events.map(eventLabel).join(", ");
}

const columnHelper = createColumnHelper<TriggerV2Record>();

/**
 * Why the table is empty. Nothing before the first load succeeds, since
 * whether there are any triggers is then unknown, or when only this page is
 * empty because a refresh removed its triggers.
 */
function NoTriggers({
  paginationState,
  filter,
}: {
  paginationState: PaginationState | undefined;
  filter: TriggerFilterParams;
}) {
  if (paginationState === undefined || paginationState.total > 0) {
    return null;
  }
  return hasActiveFilters(filter) ? (
    <span>No triggers match the current filters.</span>
  ) : (
    <span>
      No triggers yet. Click <strong>Create trigger</strong> to get started.
    </span>
  );
}

/**
 * Every trigger in the org, event-fired and schedule-fired alike, as one
 * filterable table. There is one form to create a trigger, so there is one
 * view to see them; "scheduled" is a filter, not a tab.
 *
 * Triggers are deleted from their own page, where it's clear what is being
 * deleted.
 */
export function Triggers() {
  const { goto } = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const firesOn = parseFiresOn(searchParams.get(FIRES_ON_PARAM));

  // A failed toggle is reported in the page's alert, not by its row.
  const [toggleError, setToggleError] = React.useState<Error>();

  const { currentOrganization } = useAuth();
  const agentDirectory = useAgentDirectory(currentOrganization?.org_id);
  const agentNames = React.useMemo(
    () =>
      new Map(
        agentDirectory.agents.map((agent) => [agent.agent_id, agent.name]),
      ),
    [agentDirectory.agents],
  );

  const view = useResourceView<TriggerV2Record>({
    getRowId: (row) => row.trigger_id,
    initialSort: { columnId: "modified", direction: "desc" },
  });
  const { resetCurrentPage } = view.table;
  const structuredFilters =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";

  const { sort, currentPage, pageSize } = view.table;
  const filter = { firesOn, filters: structuredFilters, matchMode };
  const triggers = useTriggers({
    sort,
    filter,
    page: currentPage,
    pageSize,
  });
  const { paginationState } = triggers;
  const error = toggleError ?? triggers.error ?? undefined;

  const selectFiresOn = (next: FiresOnFilter) => {
    setSearchParams(
      (params) => {
        if (next === "all") {
          params.delete(FIRES_ON_PARAM);
        } else {
          params.set(FIRES_ON_PARAM, next);
        }
        return params;
      },
      { replace: true },
    );
    resetCurrentPage();
  };

  const clearToggleError = React.useCallback(
    () => setToggleError(undefined),
    [],
  );

  // The primitive hides columns unless a visibility picker opts them in; this
  // list has no picker and shows every column, so flip the default.
  const columns = React.useMemo<ColDef<TriggerV2Record>[]>(() => {
    const baseColumns: ColDef<TriggerV2Record>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        valueGetter: (p) => p.data?.name ?? "",
        minWidth: 200,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) =>
          p.data ? (
            <TriggerNameLink
              triggerName={p.data.name}
              className={cellStyles.primaryContent}
            />
          ) : null,
      },
      columnHelper.accessor(firesOnSummary, {
        colId: "fires_on",
        headerName: "Fires on",
        flex: 2,
        minWidth: 120,
        sortable: false,
        cellRenderer: renderDashOrValue,
      }),
      columnHelper.accessor(
        (trigger) =>
          trigger.targets
            .map((target) => targetSummary(target, agentNames))
            .join(", "),
        {
          colId: "targets",
          headerName: "Targets",
          flex: 2,
          minWidth: 120,
          sortable: false,
          cellRenderer: renderDashOrValue,
        },
      ),
      columnHelper.timestamp("modified", {
        headerName: "Modified",
        sortable: true,
        variant: "relative",
      }),
      {
        colId: "modified_by",
        headerName: "Modified by",
        minWidth: 150,
        valueGetter: (p) => qualifyPrincipal(p.data?.modified_by),
        cellRenderer: renderPrincipal,
      },
      {
        colId: "enabled",
        headerName: "Enabled",
        flex: 0,
        width: 110,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) =>
          p.data ? (
            <TriggerEnabledSwitch
              trigger={p.data}
              onToggle={clearToggleError}
              onError={setToggleError}
            />
          ) : null,
      },
    ];
    return baseColumns.map((column) => ({ hide: false, ...column }));
  }, [agentNames, clearToggleError]);

  // Skeletons only while there's no list to show. Unlike deriveLoadingState, a
  // background refetch keeps the rows on screen.
  const loading: DataTableLoadingState = triggers.isPending
    ? "rowsOnly"
    : "none";

  return (
    <>
      <div className={styles.page}>
        {error !== undefined && (
          <Alert severity="error" className={styles.errorAlert}>
            {error.message}
          </Alert>
        )}
        <ResourceView.Root>
          <ResourceView.Actions>
            <ToggleGroup
              size="md"
              className={styles.firesOnToggle}
              aria-label="Fires on"
              value={[firesOn]}
              onValueChange={(next: FiresOnFilter[]) => {
                if (next[0] !== undefined) {
                  selectFiresOn(next[0]);
                }
              }}
            >
              <Toggle value="all">All</Toggle>
              <Toggle value="events">Platform events</Toggle>
              <Toggle value="schedule">Scheduled</Toggle>
            </ToggleGroup>
            <ResourceView.Filters
              filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
              onFilterSelected={view.commit}
            />
            <div className={styles.actionsRight}>
              <RobotoButton
                variant="primary"
                icon={<Plus aria-hidden="true" />}
                onClick={() => goto.createTrigger()}
              >
                Create trigger
              </RobotoButton>
            </div>
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<TriggerV2Record>
            table={view.table}
            ariaLabel="Triggers"
            hasNextPage={paginationState?.hasNextPage ?? false}
            loading={loading}
            columns={columns}
            defaultColDef={FLEX_FILL_COL_DEF}
            data={paginationState?.rows ?? NO_TRIGGERS}
            totalRowCount={paginationState?.total}
            noRowsContent={
              <NoTriggers paginationState={paginationState} filter={filter} />
            }
          />
        </ResourceView.Root>
      </div>
    </>
  );
}
